import { useRef, useState } from "react";
import { Link } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import type { Schema } from "hast-util-sanitize";
import { ExternalLinkIcon, Loader2Icon } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Textarea } from "@/shared/components/ui/textarea";
import type { BadgeColorMap } from "../lib/value-badge";
import { dedupeItems, parseListItems } from "../lib/list-items";
import { MatrixEntriesEditor } from "./matrix-entries-editor";
import { UnsavedChangesDialog } from "./unsaved-changes-dialog";
import {
  isColumnPrompted,
  isMatrixRowActive,
  type MatrixColumnDef,
  type MatrixRowUi,
} from "@/domains/matrix";
import {
  COLUMN_TYPE_ICONS,
  COLUMN_TYPE_INPUT_HINTS,
  COLUMN_TYPE_LABELS,
} from "../lib/column-type-meta";

const markdownSanitizeSchema: Schema = {
  ...defaultSchema,
  tagNames: [...new Set([...(defaultSchema.tagNames ?? []), "br"])],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    br: [],
  },
};

type DraftState = {
  cellId: string | null;
  source: string;
  value: string;
};

type MatrixCellSheetProps = {
  row: MatrixRowUi | null;
  column: MatrixColumnDef | null;
  badgeColors?: BadgeColorMap;
  onClose: () => void;
  onSave: (value: string) => void;
  isSaving: boolean;
  saveError: string | null;
};

export function MatrixCellSheet({
  row,
  column,
  badgeColors,
  onClose,
  onSave,
  isSaving,
  saveError,
}: MatrixCellSheetProps) {
  const cell = row && column ? row.cells[column.key] : undefined;
  const value = cell?.value ?? "";
  const cellId = row && column ? `${row.analysisId}:${column.key}` : null;
  const presentationStatus =
    column &&
    isColumnPrompted(column) &&
    cell?.status === "idle" &&
    (row?.status === "pending" || row?.status === "in progress")
      ? "pending"
      : cell?.status;

  const [draftState, setDraftState] = useState<DraftState>({
    cellId,
    source: value,
    value,
  });
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("write");
  const [resyncCount, setResyncCount] = useState(0);
  // Typed but not added yet; owned here so Save can take it along.
  const [pendingEntry, setPendingEntry] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isActive = row ? isMatrixRowActive(row) : false;

  // Another cell resets the draft; a newer server value only if nothing was typed.
  let currentDraft = draftState;
  if (draftState.cellId !== cellId) {
    currentDraft = { cellId, source: value, value };
    setDraftState(currentDraft);
    setIsConfirmingClose(false);
    setPendingEntry("");
    setActiveTab(value && column?.type === "text" ? "preview" : "write");
  } else if (
    draftState.source !== value &&
    draftState.value === draftState.source
  ) {
    currentDraft = { cellId, source: value, value };
    setDraftState(currentDraft);
  }

  const isEntryColumn = column?.type === "badge" || column?.type === "list";
  // A half-written entry counts as a change, since Save folds it in.
  const isDirty =
    !isActive &&
    (currentDraft.value !== currentDraft.source || pendingEntry.trim() !== "");

  function handleDraftChange(next: string) {
    if (isActive) return;
    setDraftState({ ...currentDraft, value: next });
  }

  function handleOpenChange(open: boolean) {
    if (open) return;
    // Clicks inside the open dialog land outside the sheet and would re-ask.
    if (isConfirmingClose) return;
    if (isDirty || isSaving) {
      setIsConfirmingClose(true);
      return;
    }
    onClose();
  }

  function handleDiscardAndClose() {
    revertDraft();
    setIsConfirmingClose(false);
    onClose();
  }

  function revertDraft() {
    setDraftState({ cellId, source: value, value });
    setPendingEntry("");
    setResyncCount((count) => count + 1);
  }

  function handleSave() {
    if (isActive) return;
    if (isEntryColumn) {
      const next = dedupeItems(
        parseListItems([currentDraft.value, pendingEntry].join(", ")),
      ).join(", ");
      setDraftState({ ...currentDraft, value: next });
      setPendingEntry("");
      setResyncCount((count) => count + 1);
      onSave(next);
      return;
    }
    onSave(currentDraft.value);
  }

  function editSource() {
    if (isActive) return;
    setActiveTab("write");
    requestAnimationFrame(() => {
      const field = textareaRef.current;
      if (!field) return;
      field.focus();
      field.setSelectionRange(field.value.length, field.value.length);
    });
  }

  const editor =
    column && (column.type === "badge" || column.type === "list") ? (
      <MatrixEntriesEditor
        value={currentDraft.value}
        onChange={handleDraftChange}
        draft={pendingEntry}
        onDraftChange={setPendingEntry}
        type={column.type}
        label={column.label}
        resetKey={`${cellId}:${resyncCount}`}
        badgeColors={badgeColors}
        readOnly={isActive}
      />
    ) : (
      <Textarea
        ref={textareaRef}
        value={currentDraft.value}
        readOnly={isActive}
        onChange={(e) => handleDraftChange(e.target.value)}
        placeholder={
          isActive
            ? "The analysis is about to fill this in..."
            : "Write anything, Markdown included..."
        }
        aria-label={`Value of ${column?.label ?? "cell"}`}
        className="min-h-[45vh] flex-1 resize-none text-sm"
      />
    );

  const hasMarkdownTabs = column?.type === "text";
  const TypeIconFor = column ? COLUMN_TYPE_ICONS[column.type] : null;

  return (
    <Sheet open={Boolean(row && column)} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 sm:max-w-2xl lg:max-w-3xl"
      >
        <SheetHeader className="border-b">
          <SheetTitle className="flex items-center gap-1.5 pr-8 text-base">
            {TypeIconFor ? (
              <TypeIconFor
                className="text-muted-foreground size-4 shrink-0"
                aria-label={
                  column ? COLUMN_TYPE_LABELS[column.type] : undefined
                }
              />
            ) : null}
            {column?.label}
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {column ? (
              <span className="inline-flex items-center gap-1">
                {COLUMN_TYPE_INPUT_HINTS[column.type].hint}
                {COLUMN_TYPE_INPUT_HINTS[column.type].separator ? (
                  <code className="bg-muted text-foreground rounded border px-1.5 py-0.5 font-mono text-[11px]">
                    {COLUMN_TYPE_INPUT_HINTS[column.type].separator}
                  </code>
                ) : null}
              </span>
            ) : null}
            {presentationStatus === "pending" ? (
              <Badge variant="secondary" className="font-normal">
                Queued
              </Badge>
            ) : presentationStatus === "in progress" ? (
              <Badge variant="secondary" className="gap-1 font-normal">
                <Loader2Icon className="size-3 animate-spin" />
                Running
              </Badge>
            ) : presentationStatus === "failed" ? (
              <Badge variant="destructive" className="font-normal">
                Failed
              </Badge>
            ) : null}
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
          {hasMarkdownTabs ? (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex min-h-0 flex-1 flex-col gap-3"
            >
              <TabsList className="self-start">
                <TabsTrigger value="write">Write</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              <TabsContent
                value="write"
                className="flex min-h-0 flex-1 flex-col"
              >
                {editor}
              </TabsContent>
              <TabsContent
                value="preview"
                className="flex min-h-0 flex-1 flex-col overflow-y-auto"
              >
                <div
                  role="button"
                  tabIndex={0}
                  title="Click to edit the source"
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("a")) return;
                    editSource();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") editSource();
                  }}
                  className="hover:bg-muted/40 focus-visible:ring-ring/50 min-h-[45vh] flex-1 cursor-text rounded-md p-1 transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                >
                  <MarkdownValue value={currentDraft.value} />
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            editor
          )}

          {saveError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not save the cell</AlertTitle>
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
          {row ? (
            <Button asChild variant="outline" className="gap-1.5">
              <Link to={`/reports/${row.analysisId}`}>
                <ExternalLinkIcon className="size-3.5" />
                Open report
              </Link>
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmingClose(true)}
              disabled={isActive || !isDirty || isSaving}
            >
              Discard
            </Button>
            <Button
              onClick={handleSave}
              disabled={isActive || !isDirty || isSaving}
            >
              {isSaving ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </div>
      </SheetContent>

      <UnsavedChangesDialog
        open={isConfirmingClose}
        onOpenChange={setIsConfirmingClose}
        onDiscard={handleDiscardAndClose}
        description="This cell has changes that were never saved. Closing the panel loses them."
      />
    </Sheet>
  );
}

function MarkdownValue({ value }: { value: string }) {
  if (!value) {
    return (
      <p className="text-muted-foreground text-sm">Nothing to preview yet.</p>
    );
  }

  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
      >
        {value}
      </ReactMarkdown>
    </div>
  );
}
